import React, { useEffect, useState, useMemo } from "react";
import api, { resolveMedia } from "@/lib/api";
import { ExternalLink, Mail, Phone, User } from "lucide-react";
import RichText from "@/components/RichText";
import useSEO from "@/lib/seo";

const CATEGORY_ORDER = ["Bauherr", "Planung", "Förderer", "Sonstige"];

export default function Partner() {
  const [page, setPage] = useState(null);
  const [partners, setPartners] = useState([]);
  useSEO({
    title: "Partner & Beteiligte",
    description: "Bauherr, Planungsbüros, Förderer und weitere Beteiligte am Quartier am Hornschen Tor in Detmold.",
    path: "/partner",
  });

  useEffect(() => {
    api.get("/partners-page").then((r) => setPage(r.data));
    api.get("/partners", { params: { only_active: true } }).then((r) => setPartners(r.data || []));
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    for (const p of partners) {
      const cat = CATEGORY_ORDER.includes(p.category) ? p.category : "Sonstige";
      if (!map[cat]) map[cat] = [];
      map[cat].push(p);
    }
    return map;
  }, [partners]);

  // Sort the full list by category order, then by `order` field
  const sortedAll = useMemo(() => {
    const catRank = (c) => {
      const i = CATEGORY_ORDER.indexOf(c);
      return i === -1 ? 99 : i;
    };
    return [...partners].sort((a, b) => {
      const cr = catRank(a.category) - catRank(b.category);
      if (cr !== 0) return cr;
      return (a.order || 0) - (b.order || 0);
    });
  }, [partners]);

  return (
    <div className="container-narrow py-16 lg:py-24 max-w-6xl" data-testid="partner-page">
      <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-4">Partner</p>
      <h1 className="font-semibold text-4xl sm:text-5xl mb-6">{page?.heading || "Unsere Partner"}</h1>
      {page?.intro && <RichText html={page.intro} className="prose-quartier text-neutral-600 mb-12 leading-relaxed max-w-2xl" />}

      {partners.length === 0 ? (
        <div className="border border-dashed border-brand-line p-12 text-center text-neutral-500">
          Es wurden noch keine Partner veröffentlicht.
        </div>
      ) : (
        <>
          {/* Logo-Übersicht (bestehend, nach Kategorie gruppiert) */}
          <div className="space-y-14 mb-20">
            {CATEGORY_ORDER.filter((c) => grouped[c]).map((cat) => (
              <section key={cat} data-testid={`partner-cat-${cat}`}>
                <h2 className="font-semibold text-xl text-neutral-800 mb-6 pb-3 border-b border-brand-line">{cat}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {grouped[cat].map((p) => (
                    <PartnerLogoCard key={p.id} partner={p} />
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* Vollständige Partnerliste mit Kontakten */}
          <section data-testid="partner-full-list" className="border-t border-brand-line pt-16">
            <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-3">Vollständige Liste</p>
            <h2 className="font-semibold text-3xl sm:text-4xl mb-3">Alle Projektbeteiligten im Überblick</h2>
            <p className="text-sm text-neutral-600 max-w-2xl mb-10 leading-relaxed">
              Vollständige Auflistung der am Quartier am Hornschen Tor mitwirkenden Unternehmen und Institutionen — mit Rolle, Beschreibung und direktem Ansprechpartner.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {sortedAll.map((p) => (
                <PartnerDetailCard key={p.id} partner={p} />
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

/** Compact logo card — used in the upper category grid */
function PartnerLogoCard({ partner }) {
  const card = (
    <div className="bg-white border border-brand-line p-6 h-full flex flex-col" data-testid={`partner-${partner.id}`}>
      <div className="h-20 mb-5 flex items-center">
        {partner.logo_url ? (
          <img src={resolveMedia(partner.logo_url)} alt={partner.name} className="max-h-20 max-w-[160px] object-contain" loading="lazy" />
        ) : (
          <span className="font-semibold text-lg text-neutral-700">{partner.name}</span>
        )}
      </div>
      <h3 className="font-semibold text-lg mb-2 text-neutral-900">{partner.name}</h3>
      {partner.description && <RichText html={partner.description} className="prose-quartier text-sm text-neutral-600 leading-relaxed flex-1" />}
      {partner.website && (
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--brand-green-strong))]">
          Website besuchen <ExternalLink className="h-3.5 w-3.5" />
        </span>
      )}
    </div>
  );
  if (partner.website) {
    return <a href={partner.website} target="_blank" rel="noreferrer" className="block hover:border-[hsl(var(--brand-green))] transition-colors">{card}</a>;
  }
  return card;
}

/** Rich detail card with logo, role, description, contact + website */
function PartnerDetailCard({ partner }) {
  return (
    <article
      className="bg-white border border-brand-line p-6 flex flex-col gap-4 h-full hover:border-[hsl(var(--brand-green))] transition-colors"
      data-testid={`partner-detail-${partner.id}`}
    >
      {/* Header: logo + name + role */}
      <header className="flex items-start gap-4">
        <div className="h-14 w-14 flex-shrink-0 border border-brand-line bg-white flex items-center justify-center overflow-hidden">
          {partner.logo_url ? (
            <img
              src={resolveMedia(partner.logo_url)}
              alt={partner.name}
              className="max-h-12 max-w-12 object-contain"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 text-center px-1">
              {partner.name.split(" ").slice(0, 2).map((w) => w[0]).join("")}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base leading-tight text-neutral-900 mb-0.5">{partner.name}</h3>
          {partner.role && (
            <p className="text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--brand-green-strong))]">{partner.role}</p>
          )}
        </div>
      </header>

      {/* Description */}
      {partner.description && (
        <p className="text-sm text-neutral-600 leading-relaxed flex-1">{stripDescription(partner.description)}</p>
      )}

      {/* Contact + website footer */}
      <footer className="pt-3 border-t border-brand-line/70 space-y-1.5 text-xs text-neutral-600">
        {partner.contact_name && (
          <div className="flex items-center gap-2">
            <User className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
            <span className="truncate text-neutral-800">{partner.contact_name}</span>
          </div>
        )}
        {partner.contact_email && (
          <div className="flex items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
            <a href={`mailto:${partner.contact_email}`} className="truncate hover:text-[hsl(var(--brand-green-strong))]">{partner.contact_email}</a>
          </div>
        )}
        {partner.contact_phone && (
          <div className="flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
            <a href={`tel:${partner.contact_phone.replace(/[^+\d]/g, "")}`} className="truncate hover:text-[hsl(var(--brand-green-strong))]">{partner.contact_phone}</a>
          </div>
        )}
        {partner.website && (
          <div className="flex items-center gap-2 pt-1">
            <ExternalLink className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
            <a
              href={partner.website}
              target="_blank"
              rel="noreferrer"
              className="truncate text-[hsl(var(--brand-green-strong))] hover:underline"
            >
              {partner.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}
            </a>
          </div>
        )}
      </footer>
    </article>
  );
}

/** Strip HTML tags from description for plain-text display in the detail cards. */
function stripDescription(html) {
  if (!html) return "";
  if (typeof document === "undefined") return html;
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
}
